// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  Video, 
  FileText, 
  Star,
  Plus,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WhiteLabelDemo {
  id: string;
  prospect_id: string;
  demo_date: string;
  demo_type: string;
  attendees: any[];
  demo_duration_minutes: number;
  demo_notes?: string;
  talking_points?: string;
  outcome?: string;
  outcome_notes?: string;
  follow_up_required: boolean;
  follow_up_date?: string;
  demo_recording_url?: string;
  feedback_score?: number;
  feedback_comments?: string;
  created_at: string;
  white_label_prospects?: {
    organization_name: string;
    contact_name: string;
    contact_email: string;
  };
}

interface WhiteLabelDemoManagerProps {
  searchTerm: string;
}

const WhiteLabelDemoManager: React.FC<WhiteLabelDemoManagerProps> = ({ searchTerm }) => {
  const [demos, setDemos] = useState<WhiteLabelDemo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchDemos();
  }, []);

  const fetchDemos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('white_label_demos')
        .select(`
          *,
          white_label_prospects (
            organization_name,
            contact_name,
            contact_email
          )
        `)
        .order('demo_date', { ascending: true });

      if (error) throw error;
      setDemos(data || []);
    } catch (error) {
      console.error('Error fetching demos:', error);
      toast.error('Failed to load demos');
    } finally {
      setLoading(false);
    }
  };

  const filteredDemos = demos.filter(demo => {
    const matchesSearch = 
      demo.white_label_prospects?.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demo.white_label_prospects?.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demo.white_label_prospects?.contact_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const now = new Date();
    const demoDate = new Date(demo.demo_date);
    
    switch (activeTab) {
      case 'upcoming':
        return matchesSearch && demoDate >= now;
      case 'completed':
        return matchesSearch && demoDate < now && demo.outcome;
      case 'follow_up':
        return matchesSearch && demo.follow_up_required && !demo.outcome;
      default:
        return matchesSearch;
    }
  });

  const getOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case 'positive': return 'default';
      case 'neutral': return 'secondary';
      case 'negative': return 'destructive';
      case 'no_show': return 'outline';
      default: return 'secondary';
    }
  };

  const getOutcomeIcon = (outcome?: string) => {
    switch (outcome) {
      case 'positive': return CheckCircle;
      case 'neutral': return AlertCircle;
      case 'negative': return XCircle;
      case 'no_show': return XCircle;
      default: return Clock;
    }
  };

  const formatDemoType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const DemoCard = ({ demo }: { demo: WhiteLabelDemo }) => {
    const OutcomeIcon = getOutcomeIcon(demo.outcome);
    const demoDate = new Date(demo.demo_date);
    const isUpcoming = demoDate >= new Date();
    
    return (
      <Card className="mb-4 hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-semibold text-sm">
                {demo.white_label_prospects?.organization_name || 'Unknown Organization'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {demo.white_label_prospects?.contact_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={getOutcomeColor(demo.outcome)}>
                <OutcomeIcon className="h-3 w-3 mr-1" />
                {demo.outcome ? formatDemoType(demo.outcome) : 'Scheduled'}
              </Badge>
              <Badge variant="outline">
                {formatDemoType(demo.demo_type)}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Demo Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span>{demoDate.toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{demoDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span>{demo.attendees?.length || 0} attendees</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{demo.demo_duration_minutes} minutes</span>
            </div>
          </div>

          {/* Attendees */}
          {demo.attendees && demo.attendees.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Attendees:</p>
              <div className="flex flex-wrap gap-1">
                {demo.attendees.slice(0, 3).map((attendee: any, index: number) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {attendee.name || attendee.email}
                  </Badge>
                ))}
                {demo.attendees.length > 3 && (
                  <Badge variant="outline" className="text-xs">
                    +{demo.attendees.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Talking Points */}
          {demo.talking_points && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Key Talking Points:</p>
              <p className="text-xs bg-muted p-2 rounded text-ellipsis overflow-hidden">
                {demo.talking_points.length > 100 
                  ? `${demo.talking_points.substring(0, 100)}...` 
                  : demo.talking_points
                }
              </p>
            </div>
          )}

          {/* Demo Notes */}
          {demo.demo_notes && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Demo Notes:</p>
              <p className="text-xs bg-muted p-2 rounded">
                {demo.demo_notes.length > 100 
                  ? `${demo.demo_notes.substring(0, 100)}...` 
                  : demo.demo_notes
                }
              </p>
            </div>
          )}

          {/* Outcome Details */}
          {demo.outcome_notes && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Outcome Notes:</p>
              <p className="text-xs bg-muted p-2 rounded">
                {demo.outcome_notes}
              </p>
            </div>
          )}

          {/* Feedback Score */}
          {demo.feedback_score && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Feedback:</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-3 w-3 ${
                      star <= demo.feedback_score! 
                        ? 'fill-yellow-400 text-yellow-400' 
                        : 'text-muted-foreground'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Follow-up Required */}
          {demo.follow_up_required && !demo.outcome && (
            <div className="flex items-center gap-2 text-orange-600">
              <AlertCircle className="h-4 w-4" />
              <span className="text-xs">Follow-up required</span>
              {demo.follow_up_date && (
                <span className="text-xs">
                  by {new Date(demo.follow_up_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Demo Recording */}
          {demo.demo_recording_url && (
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-muted-foreground" />
              <a 
                href={demo.demo_recording_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                View Recording
              </a>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              View
            </Button>
            <Button variant="outline" size="sm" className="flex items-center gap-1">
              <Edit className="h-3 w-3" />
              Edit
            </Button>
            {isUpcoming && (
              <Button size="sm" className="flex items-center gap-1">
                <Video className="h-3 w-3" />
                Join Demo
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Demo Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {demos.filter(d => new Date(d.demo_date) >= new Date()).length}
            </div>
            <div className="text-sm text-muted-foreground">Upcoming Demos</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {demos.filter(d => d.outcome === 'positive').length}
            </div>
            <div className="text-sm text-muted-foreground">Positive Outcomes</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {demos.filter(d => d.follow_up_required && !d.outcome).length}
            </div>
            <div className="text-sm text-muted-foreground">Follow-ups Needed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {demos.filter(d => d.feedback_score && d.feedback_score >= 4).length}
            </div>
            <div className="text-sm text-muted-foreground">High Satisfaction</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Demo Calendar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm flex items-center justify-between">
              Demo Calendar
              <Button size="sm" className="flex items-center gap-1">
                <Plus className="h-3 w-3" />
                Schedule
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Demo List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Demo Management</CardTitle>
            <CardDescription>Track and manage all demo activities</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                <TabsTrigger value="follow_up">Follow-up</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="space-y-4">
                {filteredDemos.length === 0 ? (
                  <div className="text-center py-8">
                    <Video className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">No demos found</p>
                    <p className="text-sm text-muted-foreground">
                      {activeTab === 'upcoming' && 'No upcoming demos scheduled'}
                      {activeTab === 'completed' && 'No completed demos yet'}
                      {activeTab === 'follow_up' && 'No follow-ups required'}
                      {activeTab === 'all' && 'Schedule your first demo to get started'}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[600px] overflow-y-auto space-y-4">
                    {filteredDemos.map(demo => (
                      <DemoCard key={demo.id} demo={demo} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WhiteLabelDemoManager;